import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Sparkles } from 'lucide-react';
import PostCard from '@/components/exchange/PostCard';
import SubmitModal from '@/components/exchange/SubmitModal';
import { fetchPosts, FILTERS } from '@/lib/exchangeApi';

const ExchangePage = ({ openSubmit = false }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('top');
  const [modalOpen, setModalOpen] = useState(openSubmit);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPosts({ category, sort });
      setPosts(data);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [category, sort]);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => {
    setModalOpen(false);
    if (window.location.pathname === '/exchange/new') navigate('/exchange', { replace: true });
  };

  const handlePublished = (post) => {
    setModalOpen(false);
    navigate(`/exchange/post/${post.id}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="exchange-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink leading-tight" data-testid="exchange-title">
            The Exchange
          </h1>
          <p className="text-ink-secondary text-base mt-2 max-w-lg">
            Prompts and skills from the AI Lab community — copy freely, post generously.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          data-testid="share-prompt-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white rounded-full font-bold text-sm hover:bg-brand-orange-hover transition-colors shrink-0 self-start md:self-auto"
        >
          <Plus size={18} strokeWidth={2.6} /> Share a prompt
        </button>
      </div>

      {/* Filters + sort */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-wrap gap-2" data-testid="filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setCategory(f)}
              data-testid={`filter-pill-${f}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                category === f
                  ? 'bg-brand-teal border-brand-teal text-white'
                  : 'bg-white border-black/10 text-ink-secondary hover:border-brand-teal/40'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 self-start rounded-full bg-black/[0.04] p-1" data-testid="sort-toggle">
          {['top', 'new'].map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              data-testid={`sort-${s}`}
              className={`px-4 py-1.5 rounded-full text-xs font-bold capitalize transition-colors ${
                sort === s ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20" data-testid="exchange-loading">
          <Loader2 size={28} className="animate-spin text-ink-muted" />
        </div>
      ) : posts.length === 0 ? (
        <div
          className="text-center bg-white rounded-3xl border border-dashed border-black/15 px-8 py-16"
          data-testid="exchange-empty-state"
        >
          <div className="w-14 h-14 rounded-2xl bg-brand-orange/10 text-brand-orange flex items-center justify-center mx-auto mb-5">
            <Sparkles size={26} />
          </div>
          <p className="text-lg font-semibold text-ink mb-1">
            Be the first to share a prompt from today's session
          </p>
          <p className="text-ink-secondary mb-6">the room is waiting.</p>
          <button
            onClick={() => setModalOpen(true)}
            data-testid="empty-share-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white rounded-full font-bold text-sm hover:bg-brand-orange-hover transition-colors"
          >
            <Plus size={18} strokeWidth={2.6} /> Share a prompt
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="exchange-post-list">
          {posts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      )}

      {modalOpen && <SubmitModal onClose={closeModal} onPublished={handlePublished} />}
    </div>
  );
};

export default ExchangePage;
